import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon from "@insite/mobius/Icon";
import Link from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ArrowRightLongIcon from "@tCentro/Icons/ArrowRightLongIcon";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import parse, { HTMLReactParserOptions } from "html-react-parser";
import React, { FC } from "react";
import { css } from "styled-components";

interface MemberList {
    fields: {
        title: string;
        description: string;
        exploreButtonLabel: string;
        exploreButtonLink: LinkFieldValue;
    };
}

const enum fields {
    divisions = "divisions",
    margin = "margin",
    // title = "title",
    // description = "description",
    // exploreButtonLabel = "exploreButtonLabel",
    // exploreButtonLink = "exploreButtonLink",
}

interface OwnProps extends WidgetProps {
    fields: {
        members: any;
        [fields.divisions]: MemberList[];
        [fields.margin]: string;
        // [fields.title]: string;
        // [fields.description]: string
        // [fields.exploreButtonLabel]: string
        // [fields.exploreButtonLink]: LinkFieldValue;
    };
}

export interface CENProductExploreStyle {
    container?: InjectableCss;
    titleText?: InjectableCss;
    description?: InjectableCss;
    buttonLabel?: InjectableCss;
    columnWrapper?: InjectableCss;
    iconStyle?: InjectableCss;
}

export const exploreStyles: CENProductExploreStyle = {
    container: {
        css: css`
            padding: 32px;
            border: 4px solid ${COLORS.background.grayShade};
            width: 100%;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        height: auto;
                    `,
                    css`
                        height: auto;
                    `,
                    css`
                        height: auto;
                    `,
                    css`
                        height: auto;
                    `,
                    null,
                ])}
        `,
    },
    titleText: {
        css: css`
            p {
                min-height: 32px;
                width: 100%;
                margin-bottom: 16px !important;
                font-size: ${TYPOGRAPHY.size.largest};
                font-weight: ${TYPOGRAPHY.weight.mediumbold};
                line-height: ${TYPOGRAPHY.lineHeight.base};
                color: ${getProp("theme.typography.p.color")};
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            font-size: ${TYPOGRAPHY.size.large};
                        `,
                        css`
                            font-size: ${TYPOGRAPHY.size.large};
                        `,
                        css`
                            height: 24px;
                            font-size: ${TYPOGRAPHY.size.large};
                        `,
                        css`
                            height: 24px;
                        `,
                        null,
                    ])}
            }
        `,
    },
    description: {
        css: css`
            p {
                min-height: 56px;
                width: 536px;
                margin-bottom: 18px !important;
                font-size: ${TYPOGRAPHY.size.large};
                font-weight: ${TYPOGRAPHY.weight.base};
                line-height: 28px;
                color: ${getProp("theme.typography.p.color")};
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            width: 100%;
                            font-size: ${TYPOGRAPHY.size.base};
                            line-height: ${TYPOGRAPHY.lineHeight.base};
                        `,
                        css`
                            display: -webkit-box;
                            -webkit-box-orient: vertical;
                            -webkit-line-clamp: 2;
                            overflow: hidden;
                            width: 100%;
                            font-size: ${TYPOGRAPHY.size.base};
                        `,
                        css`
                            display: -webkit-box;
                            -webkit-box-orient: vertical;
                            -webkit-line-clamp: 2;
                            overflow: hidden;
                            width: 100%;
                            font-size: ${TYPOGRAPHY.size.base};
                        `,
                        css`
                            width: 100%;
                            display: -webkit-box;
                            -webkit-box-orient: vertical;
                            -webkit-line-clamp: 2;
                            overflow: hidden;
                        `,
                        null,
                    ])}
            }
        `,
    },
    buttonLabel: {
        css: css`
            min-height: 16px;
            width: 100%;
            font-size: ${TYPOGRAPHY.size.smaller};
            line-height: 16px;
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            color: ${getColor("text.link")};
            cursor: pointer;
            margin-top: 16px;
        `,
    },
    iconStyle: {
        css: css`
            height: 7.5px;
            width: 12px;
            margin-left: 4px;
            svg {
                height: 7.5px;
                width: 12px;
                > path {
                    fill: ${getColor("text.link")};
                }
            }
        `,
    },
};

const styles = exploreStyles;

export const PlpPageExploreProduct: FC<OwnProps> = ({ fields }) => {
    const columnWrapper = {
        css: css`
            display: flex;
            flex-direction: row;
            gap: 24px;
            margin: ${fields.margin};
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        flex-direction: column;
                    `,
                    css`
                        flex-direction: column;
                    `,
                    null,
                    null,
                    null,
                ]);
            }}
        `,
    };

    return (
        <>
            <StyledWrapper {...columnWrapper}>
                {fields.divisions?.map(division => {
                    const { url, title } = useGetLink(division.fields.exploreButtonLink);
                    const history = useHistory();
                    const { isInShell } = useShellContext();
                    const onClick = () => {
                        if (!url) {
                            return;
                        }

                        if (url.startsWith("http")) {
                            if (isInShell) {
                                return;
                            }

                            window.location.href = url;
                        } else {
                            history.push(url);
                        }
                    };

                    return (
                        <>
                            <StyledWrapper {...styles.container}>
                                <Typography {...styles.titleText}>
                                    {parse(transformContentHtml(division.fields.title), parserOptions)}
                                </Typography>
                                <Typography {...styles.description}>
                                    {parse(transformContentHtml(division.fields.description), parserOptions)}
                                </Typography>
                                <Typography {...styles.buttonLabel} onClick={onClick}>
                                    {division.fields.exploreButtonLabel || title || url}
                                    <Icon src={ArrowRightLongIcon} {...styles.iconStyle} />
                                </Typography>
                            </StyledWrapper>
                        </>
                    );
                })}
            </StyledWrapper>
        </>
    );
};
const productExplore: WidgetModule = {
    component: PlpPageExploreProduct,
    definition: {
        group: "Basic",
        displayName: "CEN Product Explore",
        fieldDefinitions: [
            {
                name: fields.margin,
                editorTemplate: "TextField",
                displayName: "Margin",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.divisions,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.name;
                },
                defaultValue: [],
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: "title",
                        editorTemplate: "RichTextField",
                        displayName: "Title",
                        defaultValue: "",
                    },
                    {
                        name: "description",
                        editorTemplate: "RichTextField",
                        displayName: "Description",
                        defaultValue: "",
                    },
                    {
                        name: "exploreButtonLabel",
                        editorTemplate: "TextField",
                        displayName: "Button Label",
                        defaultValue: "",
                    },
                    {
                        name: "exploreButtonLink",
                        displayName: "Button Link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                    },
                ],
            },
        ],
    },
};

export default productExplore;
